"use client"

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react"
import type { CSSProperties, RefObject } from "react"
import { isFocusInside, isFocusUnclaimed } from "./focusTracking.utils"

const VIEWPORT_MARGIN = 8

type OverlaySide = "top" | "bottom"
type OverlayAlign = "start" | "center" | "end"

export function useAnchoredOverlay<
    TriggerElement extends HTMLElement = HTMLDivElement,
    PanelElement extends HTMLElement = HTMLDivElement,
>({
    isOpen,
    side = "bottom",
    align = "start",
    gap = 4,
}: {
    isOpen: boolean
    side?: OverlaySide
    align?: OverlayAlign
    gap?: number
}): {
    triggerRef: RefObject<TriggerElement | null>
    panelRef: RefObject<PanelElement | null>
    style: CSSProperties
    reposition: () => void
} {
    const triggerRef = useRef<TriggerElement | null>(null)
    const panelRef = useRef<PanelElement | null>(null)
    const [style, setStyle] = useState<CSSProperties>({
        position: "fixed",
        top: 0,
        left: 0,
        visibility: "hidden",
    })

    const position = useCallback(() => {
        const trigger = triggerRef.current
        const panel = panelRef.current
        if (!trigger || !panel) return

        const anchor = trigger.getBoundingClientRect()
        if (anchor.width === 0 && anchor.height === 0) return

        const { offsetWidth: width, offsetHeight: height } = panel
        const { innerWidth, innerHeight } = window

        const roomBelow = innerHeight - anchor.bottom
        const roomAbove = anchor.top
        const wantsBottom = side === "bottom"
        const placeBottom = wantsBottom
            ? roomBelow >= height + gap || roomBelow >= roomAbove
            : !(roomAbove >= height + gap || roomAbove >= roomBelow)

        const top = placeBottom
            ? anchor.bottom + gap
            : anchor.top - height - gap

        const left =
            align === "end"
                ? anchor.right - width
                : align === "center"
                  ? anchor.left + anchor.width / 2 - width / 2
                  : anchor.left

        setStyle({
            position: "fixed",
            top: Math.max(
                VIEWPORT_MARGIN,
                Math.min(top, innerHeight - height - VIEWPORT_MARGIN)
            ),
            left: Math.max(
                VIEWPORT_MARGIN,
                Math.min(left, innerWidth - width - VIEWPORT_MARGIN)
            ),
            visibility: "visible",
        })
    }, [side, align, gap])

    useLayoutEffect(() => {
        if (!isOpen) {
            setStyle(current => ({ ...current, visibility: "hidden" }))
            return
        }
        position()
    }, [isOpen, position])

    const isAnchorInert = useIsAnchorInert({ anchorRef: triggerRef, isOpen })

    useEffect(() => {
        if (!isOpen) return
        const handle = () => position()
        window.addEventListener("scroll", handle, true)
        window.addEventListener("resize", handle)
        return () => {
            window.removeEventListener("scroll", handle, true)
            window.removeEventListener("resize", handle)
        }
    }, [isOpen, position])

    useEffect(() => {
        const panel = panelRef.current
        if (!isOpen || !panel || typeof ResizeObserver !== "function") return
        const observer = new ResizeObserver(() => position())
        observer.observe(panel)
        return () => observer.disconnect()
    }, [isOpen, position])

    return {
        triggerRef,
        panelRef,
        style: { ...style, ...overlayStyle({ isAnchorInert }) },
        reposition: position,
    }
}

export function overlayStyle({
    isAnchorInert,
}: {
    isAnchorInert: boolean
}): CSSProperties {
    return isAnchorInert ? { visibility: "hidden", pointerEvents: "none" } : {}
}

export function useIsAnchorInert({
    anchorRef,
    isOpen,
}: {
    anchorRef: RefObject<HTMLElement | null>
    isOpen: boolean
}): boolean {
    const [isAnchorInert, setIsAnchorInert] = useState(false)
    useEffect(() => {
        if (!isOpen) {
            setIsAnchorInert(false)
            return
        }
        let isWatching = true
        const check = () => {
            if (!isWatching) return
            setIsAnchorInert(Boolean(anchorRef.current?.closest("[inert]")))
        }
        check()
        const observer = new MutationObserver(check)
        observer.observe(document.body, {
            attributes: true, // boolean-naming-exempt: external contract — MutationObserverInit option
            attributeFilter: ["inert"],
            childList: true, // boolean-naming-exempt: external contract — MutationObserverInit option
            subtree: true, // boolean-naming-exempt: external contract — MutationObserverInit option
        })
        return () => {
            isWatching = false
            observer.disconnect()
        }
    }, [isOpen, anchorRef])
    return isAnchorInert
}

export function hasFocusMovedElsewhere(panel: HTMLElement | null): boolean {
    const active = document.activeElement
    if (isFocusUnclaimed(active)) return false
    return !isFocusInside(panel, active)
}
